import _ from 'lodash';
import { globalizeSelectors } from '../Utils';
import { PriceFormatter } from 'js/mylib/Utils';
import { ShotFormatter } from 'innovatint_base/common/formatters/ShotFormatter';
import { fulfilled, rejected } from '../factories/ApiCall';
import { PRINT_TASK_DEFAULT } from '../../Constants';
import type {
  ConfigurationAction,
  ConfigurationMap,
  ConfigurationValue,
  ConfigurationState,
  ConfigurationValues,
  JsonValue,
  LabelTemplate,
  LocalPrintTask,
  PrintTaskConfig,
  PrintTaskPrinterConfig,
  SelectedCurrency,
  ShotUnitType,
  Site,
  Systemgroup,
  Zone,
} from '../types/Configuration';

const types = {
  INITIALIZE_APP: 'CONFIG/INITIALIZE_APP',

  FETCH_CONFIG: 'CONFIG/FETCH_CONFIG',
  FETCH_CONFIG_PENDING: 'CONFIG/FETCH_CONFIG_PENDING',
  FETCH_CONFIG_FULFILLED: 'CONFIG/FETCH_CONFIG_FULFILLED',
  FETCH_CONFIG_REJECTED: 'CONFIG/FETCH_CONFIG_REJECTED',
  SAVING_CONFIG: 'CONFIG/SAVING_CONFIG',

  SET_IS_STANDALONE: 'CONFIG/SET_IS_STANDALONE',
  SET_ZONE: 'CONFIG/SET_ZONE',
  SET_ZONE_FULLFILLED: 'CONFIG/SET_ZONE_FULLFILLED',
  SET_ZONE_REDUX: 'CONFIG/SET_ZONE_REDUX',
  SET_CONFIG_VALUE: 'CONFIG/SET_CONFIG_VALUE',
  SET_CONFIG_VALUE_REDUX: 'CONFIG/SET_CONFIG_VALUE_REDUX',
  UNSET_CONFIG_VALUE: 'CONFIG/UNSET_CONFIG_VALUE',
  SET_SHOTFORMATTER_UNIT: 'CONFIG/SET_SHOTFORMATTER_UNIT',
  UPDATE_SHOTFORMATTER: 'CONFIG/UPDATE_SHOTFORMATTER',
  SET_SITE: 'CONFIG/SET_SITE',
  SET_SELECTED_CURRENCY: 'CONFIG/SET_SELECTED_CURRENCY',
  SAVE_PRINT_LABELS: 'CONFIG/SAVE_PRINT_LABELS',
  SAVE_EMAIL_TEMPLATES: 'CONFIG/SAVE_EMAIL_TEMPLATES',
  SET_PRINT_LABELS: 'CONFIG/SET_PRINT_LABELS',
  SET_EMAIL_TEMPLATES: 'CONFIG/SET_EMAIL_TEMPLATES',
  FETCH_MATCHSETTING_NAMES: 'CONFIG/FETCH_MATCHSETTING_NAMES',
  FETCH_MATCHSETTING_NAMES_FULFILLED:
    'CONFIG/FETCH_MATCHSETTING_NAMES_FULFILLED',

  UPDATE_SITE: 'CONFIG/UPDATE_SITE',
  UPDATE_SITE_REJECTED: 'CONFIG/UPDATE_SITE_REJECTED',

  SEND_TEST_EMAIL: 'SERVER/SEND_TEST_EMAIL',
  SET_SYSTEMGROUPS: 'CONFIG/SET_SYSTEMGROUPS',
} as const;

function payloadAs<Payload>(action: ConfigurationAction): Payload {
  return action.payload as Payload;
}

const actions = {
  // To be called first
  initializeApp: (first_init = false): ConfigurationAction => ({
    type: types.INITIALIZE_APP,
    payload: { first_init },
  }),
  loadConfig: (bundle: unknown = undefined): ConfigurationAction => ({
    type: types.FETCH_CONFIG,
    payload: { bundle },
  }),
  setIsStandalone: (bool: boolean): ConfigurationAction<boolean> => ({
    type: types.SET_IS_STANDALONE,
    payload: bool,
  }),
  setZone: (zone: Zone): ConfigurationAction<Zone> => ({
    type: types.SET_ZONE,
    payload: zone,
  }),
  setSite: (site: Site | null): ConfigurationAction<Site | null> => ({
    type: types.SET_SITE,
    payload: site,
  }),
  setSelectedCurrency: (
    currency: SelectedCurrency
  ): ConfigurationAction<SelectedCurrency> => ({
    type: types.SET_SELECTED_CURRENCY,
    payload: currency,
  }),
  setConfig: (key: string, value: JsonValue): ConfigurationAction => ({
    type: types.SET_CONFIG_VALUE,
    payload: { key, value },
  }),
  unsetConfig: (key: string): ConfigurationAction => ({
    type: types.UNSET_CONFIG_VALUE,
    payload: { key },
  }),
  savingConfig: (flag: boolean): ConfigurationAction<boolean> => ({
    type: types.SAVING_CONFIG,
    payload: flag,
  }),
  savePrintLabels: (
    labels: LabelTemplate[]
  ): ConfigurationAction<LabelTemplate[]> => ({
    type: types.SAVE_PRINT_LABELS,
    payload: labels,
  }),
  saveEmailTemplates: (
    templates: LabelTemplate[]
  ): ConfigurationAction<LabelTemplate[]> => ({
    type: types.SAVE_EMAIL_TEMPLATES,
    payload: templates,
  }),
  setPrintLabels: (
    labels: LabelTemplate[]
  ): ConfigurationAction<LabelTemplate[]> => ({
    type: types.SET_PRINT_LABELS,
    payload: labels,
  }),
  setEmailTemplates: (
    templates: LabelTemplate[]
  ): ConfigurationAction<LabelTemplate[]> => ({
    type: types.SET_EMAIL_TEMPLATES,
    payload: templates,
  }),
  fetchMatchsettingNames: (): ConfigurationAction => ({
    type: types.FETCH_MATCHSETTING_NAMES,
  }),
  setShotformatter: (
    unit: ShotUnitType
  ): ConfigurationAction<ShotUnitType> => ({
    type: types.SET_SHOTFORMATTER_UNIT,
    payload: unit,
  }),
  updateSite: (site: Site): ConfigurationAction<Site> => ({
    type: types.UPDATE_SITE,
    payload: site,
  }),
  sendTestEmail: (recievers: string[]): ConfigurationAction<string[]> => ({
    type: types.SEND_TEST_EMAIL,
    payload: recievers,
  }),
  setSystemgroups: (
    systemgroups: Systemgroup[]
  ): ConfigurationAction<Systemgroup[]> => ({
    type: types.SET_SYSTEMGROUPS,
    payload: systemgroups,
  }),
};

const initialState: ConfigurationState = {
  config: {},
  config_values: {},
  config_start: false,
  config_saving: false,
  config_error: null,
  is_standalone: false,
  zone: null,
  site: null,
  site_update_error: null,
  // Init shotformatter
  shotFormatter: new ShotFormatter(),
  priceFormatter: new PriceFormatter(),
  send_test_email: {
    pending: false,
    error: null,
    data: null,
  },
  temporary: {
    selected_currency: null,
  },
  print_labels: [],
  email_templates: [],
  matchsetting_names: null, // null while not fetched, -> array
  systemgroups: [],
};

function reducer(
  state: ConfigurationState = initialState,
  action: ConfigurationAction
): ConfigurationState {
  switch (action.type) {
    case types.SET_IS_STANDALONE: {
      return { ...state, is_standalone: payloadAs<boolean>(action) };
    }
    case types.SAVING_CONFIG: {
      return { ...state, config_saving: payloadAs<boolean>(action) };
    }
    case types.FETCH_CONFIG_PENDING: {
      return {
        ...state,
        config: {},
        config_values: {},
        config_start: true,
        config_error: null,
      };
    }
    case types.FETCH_CONFIG_REJECTED: {
      return {
        ...state,
        config: {},
        config_values: {},
        config_start: false,
        config_error: payloadAs<{ message?: string }>(action).message,
      };
    }
    case types.FETCH_CONFIG_FULFILLED: {
      const config = payloadAs<ConfigurationMap>(action);
      return {
        ...state,
        config,
        config_values: _.mapValues(
          config,
          (val: ConfigurationValue) => val.value
        ),
        config_start: false,
        config_error: null,
        config_saving: false,
      };
    }
    case types.SET_CONFIG_VALUE_REDUX: {
      const { key, object } = payloadAs<{
        key: string;
        object: ConfigurationValue;
      }>(action);
      return {
        ...state,
        config: { ...state.config, [key]: object },
        config_values: { ...state.config_values, [key]: object.value },
      };
    }
    case types.UPDATE_SITE_REJECTED: {
      return { ...state, site_update_error: action.payload };
    }
    case types.SET_SHOTFORMATTER_UNIT: {
      return {
        ...state,
        shotFormatter: new ShotFormatter(payloadAs<ShotUnitType>(action)),
      };
    }
    case types.SET_ZONE_REDUX: {
      const zone = payloadAs<Zone>(action);
      // Setting also the selected currency to zone currency
      return {
        ...state,
        zone,
        priceFormatter: new PriceFormatter({
          ...zone,
          currencydecimals: zone.currencydecimals ?? undefined,
          currencyformat: zone.currencyformat ?? undefined,
        }),
        temporary: {
          ...state.temporary,
          selected_currency: {
            symbol: zone.currencysymbol || '',
            rate: 1,
            decimals: zone.currencydecimals,
          },
        },
      };
    }
    case types.SET_SITE: {
      return { ...state, site: payloadAs<Site>(action) };
    }
    case types.SET_SELECTED_CURRENCY: {
      return {
        ...state,
        temporary: {
          ...state.temporary,
          selected_currency: payloadAs<SelectedCurrency>(action),
        },
      };
    }
    case types.SET_PRINT_LABELS: {
      return { ...state, print_labels: payloadAs<LabelTemplate[]>(action) };
    }
    case types.SET_SYSTEMGROUPS: {
      return { ...state, systemgroups: payloadAs<Systemgroup[]>(action) };
    }
    case types.SET_EMAIL_TEMPLATES: {
      return { ...state, email_templates: payloadAs<LabelTemplate[]>(action) };
    }
    case types.FETCH_MATCHSETTING_NAMES_FULFILLED: {
      return { ...state, matchsetting_names: payloadAs<string[]>(action) };
    }
    case types.SEND_TEST_EMAIL: {
      return {
        ...state,
        send_test_email: { error: null, data: null, pending: true },
      };
    }
    case fulfilled(types.SEND_TEST_EMAIL): {
      return {
        ...state,
        send_test_email: {
          error: null,
          data: action.payload ?? null,
          pending: false,
        },
      };
    }
    case rejected(types.SEND_TEST_EMAIL): {
      return {
        ...state,
        send_test_email: { error: action.payload, data: null, pending: false },
      };
    }

    default: {
      return state;
    }
  }
}

const mountPath = 'configurations';

/**
 * Merges print_tasks_printers and print_labels to print_tasks
 * and filters tasks that are enabled and have printer and layout set
 *
 * @param {Object} state Local redux state
 * @returns {Array}
 */
function localPrintTasks(state: ConfigurationState): LocalPrintTask[] {
  const print_tasks = state.config_values.print_tasks as
    | PrintTaskConfig[]
    | undefined;
  const print_tasks_printer = state.config_values.print_tasks_printer as
    | PrintTaskPrinterConfig[]
    | undefined;

  const { print_labels } = state;

  // Pick default printer from "Default" task
  const def_printer = _.find(
    print_tasks_printer,
    (x: PrintTaskPrinterConfig) => x.task === PRINT_TASK_DEFAULT
  )?.printer;

  return print_tasks
    ? print_tasks
        .map((task: PrintTaskConfig): LocalPrintTask => {
          const { printer, enabled = false } =
            _.find(
              print_tasks_printer,
              (x: PrintTaskPrinterConfig) => x.task === task.name
            ) || {};

          const layout = _.find(
            print_labels,
            (x: LabelTemplate) => x.name === task.template
          );

          return { ...task, printer: printer || def_printer, enabled, layout };
        })
        .filter((x) => x.enabled && x.printer && x.layout)
    : [];
}

const localSelectors = {
  is_standalone: (state: ConfigurationState): boolean => state.is_standalone,
  zone: (state: ConfigurationState): Zone | null => state.zone,
  site: (state: ConfigurationState): Site | null => state.site,
  config: (state: ConfigurationState): ConfigurationMap => state.config,
  config_values: (state: ConfigurationState): ConfigurationValues =>
    state.config_values,
  shotFormatter: (state: ConfigurationState): ShotFormatter =>
    state.shotFormatter,
  priceFormatter: (state: ConfigurationState): PriceFormatter =>
    state.priceFormatter,
  shotUnit: (state: ConfigurationState): string =>
    state.shotFormatter.unit.unitname,
  has_order_pool: (state: ConfigurationState): boolean =>
    !!state?.site?.poolsiteid &&
    state?.site?.siteid !== state?.site?.poolsiteid,
  selected_currency: (state: ConfigurationState): SelectedCurrency | null =>
    state.temporary.selected_currency,
  print_labels: (state: ConfigurationState): LabelTemplate[] =>
    state.print_labels,
  localclient: (state: ConfigurationState): Site['localclient'] | undefined =>
    state.site?.localclient,
  email_templates: (state: ConfigurationState): LabelTemplate[] =>
    state.email_templates,
  localPrintTasks,
  base_dosing: (state: ConfigurationState): JsonValue | undefined =>
    state.config.enable_base_dosing?.value,
};

const selectors = globalizeSelectors(localSelectors, mountPath);

export { types as actionTypes, actions as default, selectors, reducer };
